import { FC, useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  styled,
  Container,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { useAuth0Token, getUserInfo } from "../common/http-requests";
import { userInfoRequestType, userInfoResponseType } from "../common/type";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { Toolbar } from "../components/FooterToolbar";
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { themeColors } from "../common/colors";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { pageMt } from "../common/util";
import { useUserId } from "../hooks/useStoreGetters";

const LINE_CHART_SIZE: number[] = [
  800, 320
];

const Title = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${LINE_CHART_SIZE[0]}px`,
  margin: 'auto',
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
  textAlign: 'left',
  marginBottom: '2rem',
}));

const StyledLabel = styled('label')({
  position: 'relative',
  width: '100%',
  color: `${themeColors[0]}`,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: `${themeColors[0]}`,
  },
});

const StyledTypography = styled(Typography)({
  position: 'relative',
  width: '100%',
  color: `${themeColors[0]}`,
  lineHeight: '1.6rem',
  minWidth: '2em',
  minHeight: '1.6rem',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: `${themeColors[0]}`,
  },
});

const UserInfoPage: FC = () => {
  const { t } = useTranslation();
  const { isDesktop } = useCustomMediaQuery();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const userId = useUserId();

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  const [formData, setFormData] = useState({
    student_number: '',
    user_name: '',
    department_name: '',
    major_name: '',
    course_name: '',
    academic_degree_name: '',
    license_names: '',
  });

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getUserInfoData(userId);
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, [userId]);

  const getUserInfoData = async (
    login_user_id: number, // userResponseType.id の値
  ) => {
    setIsSending(true);
    try {
      const requestParams: userInfoRequestType = {
        login_user_id,
      };
      const data: userInfoResponseType = await getUserInfo(
        await getToken(),
        requestParams
      );
      setFormData({
        "student_number": data.student_number,
        "user_name": data.user_name,
        "department_name": data.department_name,
        "major_name": data.major_name,
        "course_name": data.course_name,
        "academic_degree_name": data.academic_degree_name,
        'license_names': data.license_names.join(',')
      })
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Container maxWidth="xl">
      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <>
            <VisualizedMenus />
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            ></FeedbackBar>
            <Box mt={pageMt(isDesktop)} className="mb-2 p-2 w-[80%] mx-auto" maxWidth={`${LINE_CHART_SIZE[0]}px`}>
              <Title>
                <Typography
                  variant="h5"
                  style={{ marginTop: 10, textAlign: "left" }}
                >
                  <BadgeOutlinedIcon sx={{ margin: '.5rem .8rem' }} />{t("menu.userInfo")}
                </Typography>
              </Title>
              <FieldComponent label={t("userInfo.number")} value={formData.student_number} />
              <FieldComponent label={t("userInfo.username")} value={formData.user_name} />
              <FieldComponent label={t("userInfo.departmentName")} value={formData.department_name} />
              <FieldComponent label={t("userInfo.majorName")} value={formData.major_name} />
              <FieldComponent label={t("userInfo.courseName")} value={formData.course_name} />
              <FieldComponent label={t("userInfo.academicDegreeName")} value={formData.academic_degree_name} />
              <FieldComponent label={t("userInfo.licenseName")} value={formData.license_names} />
              <Box
                sx={{
                  width: '20%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 'fit-content',
                  margin: '2rem auto',
                }}
              >
              </Box>
            </Box>
          </>
        )
      }
      <Toolbar />
    </Container >
  );
};

export default UserInfoPage;

interface FieldComponentProps {
  label: string;
  value: string;
}

const FieldComponent: React.FC<FieldComponentProps> = ({
  label,
  value
}) => (
  <Grid container spacing={2} my={2}>
    <Grid item xs={12}>
      <Grid container alignItems="center">
        <Grid item xs={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <StyledLabel>
              {label}
            </StyledLabel>
          </Box>
        </Grid>
        <Grid item xs={0.3}></Grid>
        <Grid item xs={7.7} position={'relative'}>
          <StyledTypography>{value}</StyledTypography>
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);